import { useCallback, useState } from 'react';
import {
    deriveSmriqJourneyViewState,
    type SmriqJourneyViewStateInput,
} from '../utils/smriqJourneyViewState';

/** Derives the journey view and tracks whether the mounted plan-controls form holds an unsent
 *  draft; a pending draft holds off the automatic journey so the form stays put. */
export function useSmriqPlanFieldsDraft(
    markStale: () => void,
    viewInput: SmriqJourneyViewStateInput,
) {
    const [edited, setEdited] = useState(false);
    const [draftStartDate, setDraftStartDate] = useState<string | null>(null);
    const view = deriveSmriqJourneyViewState({
        ...viewInput,
        generationBlocked: viewInput.generationBlocked || edited,
    });
    const controlsMounted =
        viewInput.confirmed && viewInput.hasContext && !view.retrievingPlan && view.showPlanControls;
    // Unmounting the plan controls discards their draft, so the guard cannot outlive them.
    if (!controlsMounted && (edited || draftStartDate !== null)) {
        setEdited(false);
        setDraftStartDate(null);
    }
    const planFieldsEdited = edited && controlsMounted;

    const handlePlanFieldsChange = useCallback(() => {
        setEdited(true);
        markStale();
    }, [markStale]);
    const handlePlanStartDateChange = useCallback(
        (date: string) => setDraftStartDate(date || null),
        [],
    );
    const clearPlanFieldsEdited = useCallback(() => {
        setEdited(false);
        setDraftStartDate(null);
    }, []);

    return {
        ...view,
        planFieldsEdited,
        planStartDraft: planFieldsEdited ? draftStartDate : null,
        handlePlanFieldsChange,
        handlePlanStartDateChange,
        clearPlanFieldsEdited,
    };
}
